import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  Play,
  Pause,
  Square,
  BarChart3,
  Filter,
  Search,
  Plus,
  Timer,
  Target,
  TrendingUp,
  ChevronRight,
  Eye
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface Task {
  id: string;
  title: string;
  description?: string;
  clientName: string;
  status: 'DRAFT' | 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'SUBMITTED';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string;
  assignedRole: 'preparer' | 'reviewer' | 'submitter';
  timeSpent: number; // in minutes
  estimatedTime?: number; // in minutes
  completionPercentage: number;
  type: string;
}

interface TimeEntry {
  id: string;
  taskId: string;
  taskTitle: string;
  startTime: string;
  endTime?: string;
  duration: number; // in minutes
  description?: string;
}

interface StaffStats {
  assignedTasks: number;
  completedToday: number;
  hoursWorkedToday: number;
  overdueTasksCount: number;
  weeklyHours: number;
  completionRate: number;
}

export const StaffTaskDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [stats, setStats] = useState<StaffStats>({
    assignedTasks: 0,
    completedToday: 0,
    hoursWorkedToday: 0,
    overdueTasksCount: 0,
    weeklyHours: 0,
    completionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => {
    fetchStaffData();
  }, []);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      // Fetch assigned tasks with workflow filtering
      try {
        const tasksResponse = await api.get('/tasks/workflow');
        if (tasksResponse.data.success) {
          setTasks(tasksResponse.data.data || []);
        }
      } catch (workflowError) {
        // Fallback to regular assigned tasks if workflow endpoint fails
        try {
          const fallbackResponse = await api.get('/tasks/assigned');
          if (fallbackResponse.data.success) {
            setTasks(fallbackResponse.data.data || []);
          }
        } catch (assignedError) {
          console.error('Error fetching assigned tasks:', assignedError);
        }
      }

      // Fetch time entries
      try {
        const timeResponse = await api.get('/time-entries/my-entries');
        if (timeResponse.data.success) {
          setTimeEntries(timeResponse.data.data || []);
        }
      } catch (timeError) {
        console.error('Error fetching time entries:', timeError);
      }

      // Calculate stats
      calculateStats();
    } catch (error) {
      console.error('Error fetching staff data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const today = new Date().toDateString();
    const thisWeekStart = new Date();
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());

    const completedToday = tasks.filter(task => 
      task.status === 'COMPLETED' && 
      new Date(task.dueDate).toDateString() === today
    ).length;

    const overdueTasksCount = tasks.filter(task => 
      new Date(task.dueDate) < new Date() && 
      !['COMPLETED', 'SUBMITTED'].includes(task.status)
    ).length;

    const hoursWorkedToday = timeEntries
      .filter(entry => new Date(entry.startTime).toDateString() === today)
      .reduce((total, entry) => total + entry.duration, 0) / 60;

    const weeklyHours = timeEntries
      .filter(entry => new Date(entry.startTime) >= thisWeekStart)
      .reduce((total, entry) => total + entry.duration, 0) / 60;

    const completedTasks = tasks.filter(task => ['COMPLETED', 'SUBMITTED'].includes(task.status)).length;
    const completionRate = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;

    setStats({
      assignedTasks: tasks.length,
      completedToday,
      hoursWorkedToday,
      overdueTasksCount,
      weeklyHours,
      completionRate
    });
  };

  const startTimer = (taskId: string) => {
    setActiveTimer(taskId);
    toast.success('Timer started');
  };

  const stopTimer = (taskId: string) => {
    setActiveTimer(null);
    toast.success('Timer stopped');
  };

  const updateTaskStatus = async (taskId: string, newStatus: string) => {
    try {
      const response = await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
      if (response.data.success) {
        setTasks(tasks.map(task =>
          task.id === taskId ? { ...task, status: newStatus as any } : task
        ));
        toast.success('Task status updated');
        calculateStats();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'REVIEW': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'SUBMITTED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };



  const getWorkflowActionButton = (task: any) => {
    const { status, assignedRole } = task;

    // Preparer actions
    if (assignedRole === 'preparer') {
      if (status === 'draft') {
        return (
          <button
            onClick={() => updateTaskStatus(task.id, 'in_progress')}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Start Work
          </button>
        );
      }
      if (status === 'in_progress') {
        return (
          <button
            onClick={() => updateTaskStatus(task.id, 'in_review')}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Send for Review
          </button>
        );
      }
    }

    // Reviewer actions
    if (assignedRole === 'reviewer' && status === 'in_review') {
      return (
        <div className="flex space-x-1">
          <button
            onClick={() => updateTaskStatus(task.id, 'ready_to_submit')}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Approve
          </button>
          <button
            onClick={() => updateTaskStatus(task.id, 'in_progress')}
            className="px-3 py-1 text-xs bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
          >
            Request Changes
          </button>
        </div>
      );
    }

    // Submitter actions
    if (assignedRole === 'submitter') {
      if (status === 'ready_to_submit') {
        return (
          <button
            onClick={() => updateTaskStatus(task.id, 'submitted')}
            className="px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Submit
          </button>
        );
      }
      if (status === 'submitted') {
        return (
          <button
            onClick={() => updateTaskStatus(task.id, 'completed')}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Mark Complete
          </button>
        );
      }
    }

    return null;
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {user?.firstName}! Here are your assigned tasks.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                <Plus className="w-4 h-4 mr-2" />
                Request Task
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Assigned Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.assignedTasks}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed Today</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedToday}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Hours Today</p>
                <p className="text-2xl font-bold text-gray-900">{stats.hoursWorkedToday.toFixed(1)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Overdue Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overdueTasksCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="REVIEW">Review</option>
                <option value="COMPLETED">Completed</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">My Tasks</h2>
            <div className="space-y-4">
              {filteredTasks.map((task) => (
                <div key={task.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        <span className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority.toUpperCase()}
                        </span>
                        {task.assignedRole && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {task.assignedRole.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{task.clientName}</p>
                      {task.description && (
                        <p className="text-sm text-gray-500 mb-2">{task.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {Math.floor(task.timeSpent / 60)}h {task.timeSpent % 60}m
                        </span>
                        <span className="flex items-center">
                          <Target className="w-3 h-3 mr-1" />
                          {task.completionPercentage}% complete
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {activeTimer === task.id ? (
                        <button
                          onClick={() => stopTimer(task.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Stop Timer"
                        >
                          <Square className="w-4 h-4 fill-current" />
                        </button>
                      ) : (
                        <button
                          onClick={() => startTimer(task.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Start Timer"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                        <Eye className="w-4 h-4" />
                      </button>
                      {getWorkflowActionButton(task)}
                      <select
                        value={task.status}
                        onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="TODO">To Do</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="REVIEW">Review</option>
                        <option value="COMPLETED">Completed</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              {filteredTasks.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No tasks found matching your criteria.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
